<template>
  <div>
    <EdroneTooltip
      :disabled="snapshot.hasTag('loading')"
      :offset="[0, 16]"
      close-trigger-class-name="close-status"
      position="bottom"
      trigger-event="mouseenter"
      type="custom"
    >
      <PackageIndicatorStatus
        :icon="icon"
        :loading="snapshot.hasTag('loading')"
        :progress="progress"
        size="tw:size-10"
        @click="isFreemiumSMS || isFreemiumWhatsApp ? handleUpgradeToPaidPlan() : $emit('openPackageManager')"
      />

      <template #custom>
        <div class="tw:flex tw:flex-col tw:gap-4 tw:p-2 tw:min-w-[16.25rem]">
          <div v-if="!isFreemiumWhatsApp" class="tw:flex tw:flex-col tw:gap-4">
            <div class="tw:flex tw:flex-col">
              <p class="tw:body-2 tw:mb-1">{{ trans._packageManager.credits.label }}</p>
              <p class="tw:heading-1 tw:mb-2">{{ snapshot.context.data.totalCredits }}</p>
              <p v-if="snapshot.context?.data.renewalDate !== null" class="tw:body-2 tw:text-default-700">
                {{ trans._packageManager.credits.renewal }}
                <b>
                  {{ getRenewalDate(snapshot.context?.data.renewalDate) }}
                </b>
              </p>
            </div>
            <EdroneAlert
              v-if="snapshot.context?.data.freePackageClaimed === false"
              :desc="trans._packageManager.freePackage.sms.description"
              :title="trans._packageManager.freePackage.sms.title"
              type="tip"
            />
          </div>
          <EdroneButton
            v-if="snapshot.context.isIntegrated && !isFreemiumSMS && !isFreemiumWhatsApp"
            class="close-status tw:w-full"
            @click="$emit('openPackageManager')"
          >
            {{ trans._packageManager.buttons.upgrade }}
            <template #right-icon>
              <FontAwesomeIcon icon="fa-light fa-rocket-launch" />
            </template>
          </EdroneButton>
          <div
            v-else-if="isFreemiumSMS || isFreemiumWhatsApp"
            class="tw:flex tw:flex-col tw:gap-2 tw:items-center"
            :class="{ 'tw:border-t tw:border-default-200': isFreemiumSMS }"
          >
            <img class="tw:w-20 tw:-mb-3" src="https://d3vhsxl1pwzf0p.cloudfront.net/logoUpgrade.svg" alt="" />
            <div>
              <p class="tw:body-2--bold tw:text-black tw:text-center">
                {{
                  isFreemiumWhatsApp
                    ? trans._packageManager.freemiumUpgrade.whatsapp.title
                    : trans._packageManager.freemiumUpgrade.sms.title
                }}
              </p>
              <p class="tw:body-2 tw:text-default-700 tw:text-center">
                {{
                  isFreemiumWhatsApp
                    ? trans._packageManager.freemiumUpgrade.whatsapp.description
                    : trans._packageManager.freemiumUpgrade.sms.description
                }}
              </p>
            </div>
            <EdroneButton class="close-status tw:w-full tw:!py-6" @click="handleUpgradeToPaidPlan">
              <div class="tw:w-full tw:text-wrap">{{ trans._billing.upgradeToByMoreCredits }}</div>
            </EdroneButton>
          </div>
          <EdroneButton v-else class="close-status tw:w-full" @click="goToIntegrationPage">
            {{ trans._packageManager.buttons.integration }}
            <template #right-icon>
              <FontAwesomeIcon icon="fa-light fa-plug" />
            </template>
          </EdroneButton>
        </div>
      </template>
    </EdroneTooltip>
  </div>
</template>

<script lang="ts" setup>
import EdroneTooltip from "edrone-components/EdroneTooltip.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import { computed, inject } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { getRenewalDate, getSubscriptionPackageSize } from "vue-app/components/PackageManager/PackageManager.helpers";
import PackageIndicatorStatus from "vue-app/components/PackageIndicator/PackageIndicatorStatus.vue";
import { usePackageManagerMachine } from "vue-app/components/PackageManager/PackageManager.machine.ts";
import { useRouter } from "vue-router";
import EdroneAlert from "edrone-components/EdroneAlert.vue";
import { CHANNEL_TYPE } from "vue-app/types/enums";
import { symbolMachineFreemium } from "vue-app/components/Freemium/Freemium.machine";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";

interface Props {
  channelTypeId: number;
  icon: string;
}
const props = defineProps<Props>();
defineEmits(["openPackageManager"]);
const router = useRouter();
const { snapshot } = usePackageManagerMachine(props.channelTypeId);
const isPaying = window.EDRONE_CONTEXT.tracker.isPaying;
const freemiumActor = inject(symbolMachineFreemium);

const progress = computed(() => {
  const packageSize = getSubscriptionPackageSize(
    snapshot.value.context.data.subscription.packageSize,
    snapshot.value.context.data.credits
  );
  const percentage = Math.min(snapshot.value.context.data.totalCredits / packageSize, 1);
  return Math.round(percentage * 100) || 0;
});

const isFreemiumSMS = computed(() => !isPaying && props.channelTypeId === CHANNEL_TYPE.SMS);
const isFreemiumWhatsApp = computed(() => !isPaying && props.channelTypeId === CHANNEL_TYPE.WHATSAPP);

function handleUpgradeToPaidPlan() {
  const eventName = isFreemiumSMS.value
    ? "SMS_Freemium_Upgrade_Clicked_From_Status"
    : "WhatsApp_Freemium_Upgrade_Clicked_From_Status";
  amplitudeSendEvent(eventName);
  freemiumActor?.send({ type: "UPGRADE_PLAN" });
}

const goToIntegrationPage = () => {
  if (snapshot.value.context.integrationUrl) {
    router.push({ path: snapshot.value.context.integrationUrl });
  }
};
</script>
